import { useEffect, useState } from "react";

export type UberShippingQuoteOutcome =
	| "available"
	| "location"
	| "skipped"
	| "unasked";

interface UseUberShippingQuoteParams {
	vendorId: string | number | undefined;
	/** Cookie address and customer phone. A change refetches. The server ignores this. */
	addressKey: string;
	enabled: boolean;
}

function toVendorId(vendorId: string | number | undefined): number | null {
	const parsed = Number(vendorId);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Checkout-only Uber quote. Runs after the delivery address or phone changes,
 * never while the customer is still typing. Only an `available` outcome leaves
 * the Uber card selectable.
 */
export function useUberShippingQuote({
	vendorId,
	addressKey,
	enabled,
}: UseUberShippingQuoteParams): {
	outcome: UberShippingQuoteOutcome | null;
} {
	const numericVendorId = toVendorId(vendorId);
	const canQuote = enabled && numericVendorId !== null;
	const [outcome, setOutcome] = useState<UberShippingQuoteOutcome | null>(null);

	useEffect(() => {
		if (!canQuote || numericVendorId === null) {
			setOutcome(null);
			return;
		}
		const controller = new AbortController();
		const savedAddressKey = addressKey;
		let discarded = false;
		// Dev StrictMode runs this effect, its cleanup, and the effect again in
		// one turn. fetch() here would already be inside requestShippingQuote
		// before abort arrives, so the discarded mount must not send.
		queueMicrotask(() => {
			if (discarded) {
				return;
			}
			const params = new URLSearchParams({
				vendorId: String(numericVendorId),
			});
			fetch(`/api/uber-shipping-quote?${params.toString()}`, {
				signal: controller.signal,
				credentials: "same-origin",
			})
				.then(async (response) => {
					if (!response.ok) {
						return { outcome: "unasked" as const };
					}
					return (await response.json()) as {
						outcome?: UberShippingQuoteOutcome;
					};
				})
				.then((body) => {
					if (controller.signal.aborted || savedAddressKey !== addressKey) {
						return;
					}
					const next = body.outcome;
					setOutcome(
						next === "available" ||
							next === "location" ||
							next === "skipped" ||
							next === "unasked"
							? next
							: "unasked",
					);
				})
				.catch(() => {
					if (!controller.signal.aborted) {
						setOutcome("unasked");
					}
				});
		});
		return () => {
			discarded = true;
			controller.abort();
		};
	}, [canQuote, numericVendorId, addressKey]);

	return { outcome: canQuote ? outcome : null };
}
